import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, Loader2, Navigation, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { BRAND, COUNTRIES } from "@/data/content";
import { buildEnquiryMessage, openWhatsApp } from "@/lib/whatsapp";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Enquiry",
    country: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHONE_RE = /^[0-9+\-\s()]{7,16}$/;

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    else if (!PHONE_RE.test(form.phone.trim())) next.phone = "Enter a valid phone number.";
    if (!form.message.trim()) next.message = "Please tell us a bit about your goals.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validate()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      // Brief pause so the loading state is visible before hand-off to WhatsApp.
      await new Promise((resolve) => setTimeout(resolve, 400));
      const message = buildEnquiryMessage({
        name: form.name,
        phone: form.phone,
        email: form.email,
        course: form.subject,
        country: form.country,
        message: form.message,
      });
      toast.success("Thanks! Opening WhatsApp with your details…");
      openWhatsApp(message);
      setForm({ name: "", email: "", phone: "", subject: "General Enquiry", country: "", message: "" });
      setErrors({});
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BRAND.address)}`;
  const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(BRAND.address)}&output=embed`;

  return (
    <section id="contact" data-testid="contact-section" className="section bg-[#F8F9FA]">
      <div className="container-x">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#0A3161]/15 bg-white px-3 py-1 text-[12px] font-semibold text-[#0A3161]">
            Contact Us
          </div>
          <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-slate-900" data-testid="contact-heading">
            Let's plan your <span className="text-[#B31942]">next big move</span>.
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Speak to a career counselor — completely free, no obligation.
          </p>
        </motion.div>

        {/* Form + Map, side by side */}
        <div className="mt-10 md:mt-14 grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <motion.form
            id="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white border border-black/5 p-6 md:p-8 soft-shadow-lg flex flex-col"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required error={errors.name}>
                <input data-testid="input-name" value={form.name} onChange={update("name")} className={fieldClass(errors.name)} placeholder="Your full name" />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input type="email" data-testid="input-email" value={form.email} onChange={update("email")} className={fieldClass(errors.email)} placeholder="you@example.com" />
              </Field>
              <Field label="Phone" required error={errors.phone}>
                <input data-testid="input-phone" value={form.phone} onChange={update("phone")} className={fieldClass(errors.phone)} placeholder="+91 98765 43210" />
              </Field>
              <Field label="I'm interested in">
                <select data-testid="input-subject" value={form.subject} onChange={update("subject")} className={fieldClass()}>
                  <option>General Enquiry</option>
                  <option>Technology Courses</option>
                  <option>Study Abroad</option>
                  <option>Placement Assistance</option>
                  <option>Corporate Training</option>
                </select>
              </Field>
              <Field label="Preferred Country">
                <select data-testid="input-country" value={form.country} onChange={update("country")} className={fieldClass()}>
                  <option value="">Not sure yet</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Message" required error={errors.message}>
              <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={5} className={`${fieldClass(errors.message)} !h-auto p-4 resize-none`} placeholder="Tell us your goals and preferred program…" />
            </Field>
            <div className="mt-auto pt-2 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500">We respond within 24 business hours. Your info stays private.</p>
              <motion.button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 rounded-full bg-[#B31942] text-white px-6 py-3 text-sm font-semibold hover:bg-[#940835] disabled:opacity-70 transition-colors"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending…" : "Send via WhatsApp"}
              </motion.button>
            </div>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.01 }}
            className="rounded-[20px] overflow-hidden border border-black/5 soft-shadow-lg bg-white transition-transform min-h-[320px] lg:min-h-0"
          >
            <iframe
              title="IIUTCStudy Location"
              className="w-full h-full min-h-[320px] lg:min-h-[450px]"
              src={mapEmbedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>

        {/* Office info + actions */}
        <div className="mt-8 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white border border-black/5 soft-shadow p-6 md:p-8 grid sm:grid-cols-2 gap-5 text-left"
          >
            <InfoRow icon={<MapPin className="h-5 w-5" />} label="Office Address" value={BRAND.address} testId="contact-address" />
            <InfoRow icon={<Phone className="h-5 w-5" />} label="Phone" value={BRAND.phone} href={`tel:${BRAND.phone.replace(/\s/g, "")}`} testId="contact-phone" />
            <InfoRow icon={<Mail className="h-5 w-5" />} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testId="contact-email" />
            <InfoRow icon={<Clock className="h-5 w-5" />} label="Working Hours" value={BRAND.hours} testId="contact-hours" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-3"
          >
            <motion.a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="get-directions"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border-2 border-[#0A3161] text-[#0A3161] hover:bg-[#0A3161] hover:text-white transition-colors"
            >
              <Navigation className="h-4 w-4" /> Get Directions
            </motion.a>
            <motion.a
              href="#contact-form"
              data-testid="book-consultation"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-[#B31942] text-white hover:bg-[#940835] shadow-[0_10px_30px_rgba(179,25,66,0.3)] transition-colors"
            >
              <CalendarCheck className="h-4 w-4" /> Book Free Consultation
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, error, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">
        {label} {required && <span className="text-[#B31942]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs font-medium text-red-500">{error}</span>}
    </label>
  );
}

function fieldClass(error) {
  return `w-full h-12 rounded-xl border bg-white px-4 text-sm focus:outline-none focus:ring-2 transition-colors ${
    error
      ? "border-red-400 focus:border-red-400 focus:ring-red-400/15"
      : "border-black/10 focus:border-[#0A3161] focus:ring-[#0A3161]/15"
  }`;
}

function InfoRow({ icon, label, value, href, testId }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testId}
      className={`flex items-start gap-3 ${href ? "hover:text-[#0A3161] transition-colors" : ""}`}
    >
      <span className="h-11 w-11 shrink-0 rounded-xl bg-[#0A3161]/10 text-[#0A3161] inline-flex items-center justify-center">
        {icon}
      </span>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-400">{label}</div>
        <div className="font-semibold text-slate-900 leading-snug">{value}</div>
      </div>
    </Wrapper>
  );
}
